import { Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { Skeleton } from '@ghs/components';
import {
  AgentsNextSteps,
  CustomerHelpScenario,
  CustomerSupportGuidanceProps,
  CustomerSupportGuidanceTableRowProps,
  CustomerSupportImpact,
  SampleQuestion,
  ScenarioDescription,
  SimpleExplanation
} from './types.js';

/**
 * CustomerSupportGuidance component is used for grouping and displaying the certain data from the
 * searchResults object in a table. If there is a request error then it will display a table skeleton.
 */
const CustomerSupportGuidance = ({ searchResults, isRequestError }: CustomerSupportGuidanceProps) => {
  return <CustomerSupportGuidanceDataTable searchResults={searchResults} isRequestError={isRequestError} />;
};

const TableFromCustomerHelpScenario = ({ customerHelpScenario }: { customerHelpScenario: CustomerHelpScenario }) => {
  return (
    <TableContainer component={Paper}>
      <Table size={'small'}>
        <TableBody>
          {customerHelpScenario.scenarioDescription.description?.trim() && (
            <CustomerSupportGuidanceTableRow rowKey={'scenarioDescription'} label={'Scenario Description:'}>
              <HelpScenarioDescriptionAsTextAndNoteFormatForScenarioDescription scenarioDescription={customerHelpScenario.scenarioDescription} />
            </CustomerSupportGuidanceTableRow>
          )}
          {customerHelpScenario.customerSupportImpact.description?.trim() && (
            <CustomerSupportGuidanceTableRow rowKey={'customerSupportImpact'} label={'Customer Support Impact:'}>
              <HelpScenarioDescriptionAsTextFormatForCustomerSupportImpact customerSupportImpact={customerHelpScenario.customerSupportImpact} />
            </CustomerSupportGuidanceTableRow>
          )}
          {customerHelpScenario.simpleExplanation.explanation?.trim() && (
            <CustomerSupportGuidanceTableRow rowKey={'simpleExplanation'} label={'Simple Explanation:'}>
              <HelpScenarioDescriptionAsTextFormatForSimpleExplanation simpleExplanation={customerHelpScenario.simpleExplanation} />
            </CustomerSupportGuidanceTableRow>
          )}
          {customerHelpScenario.agentsNextSteps.steps?.length > 0 && (
            <CustomerSupportGuidanceTableRow rowKey={'agentsNextSteps'} label={"Agent's Next Steps:"}>
              <HelpScenarioDescriptionAsListFormatForAgentsNextSteps agentsNextSteps={customerHelpScenario.agentsNextSteps} />
            </CustomerSupportGuidanceTableRow>
          )}
          {customerHelpScenario.sampleQuestions.questions?.length > 0 && (
            <CustomerSupportGuidanceTableRow rowKey={'sampleQuestions'} label={'Sample Questions:'}>
              <HelpScenarioDescriptionAsListFormatForSampleQuestions sampleQuestions={customerHelpScenario.sampleQuestions} />
            </CustomerSupportGuidanceTableRow>
          )}
          {customerHelpScenario.additionalAddressIssues?.length > 0 && (
            <CustomerSupportGuidanceTableRow rowKey={'additionalAddressIssues'} label={'Additional Address Issues:'}>
              <HelpScenarioDescriptionAsListFormatForAdditionalAddressIssues additionalAddressIssues={customerHelpScenario.additionalAddressIssues} />
            </CustomerSupportGuidanceTableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TableSkeleton = () => {
  return (
    <TableContainer component={Paper}>
      <Table size={'small'}>
        <TableBody>
          <CustomerSupportGuidanceTableSkeletonRow rowName={'Scenario Description:'} />
          <CustomerSupportGuidanceTableSkeletonRow rowName={'Customer Support Impact:'} />
          <CustomerSupportGuidanceTableSkeletonRow rowName={'Simple Explanation:'} />
          <CustomerSupportGuidanceTableSkeletonRow rowName={"Agent's Next Steps:"} />
          <CustomerSupportGuidanceTableSkeletonRow rowName={'Sample Questions:'} />
          <CustomerSupportGuidanceTableSkeletonRow rowName={'Additional Address Issues:'} />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const HelpScenarioDescriptionAsTextAndNoteFormatForScenarioDescription = ({ scenarioDescription }: { scenarioDescription: ScenarioDescription }) => {
  const { description, note } = scenarioDescription;

  return (
    <div>
      <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
        {description}
      </Typography>
      {note && (
        <Typography variant="body2" color={'error.main'} style={{ whiteSpace: 'pre-line', fontStyle: 'italic' }}>
          {note}
        </Typography>
      )}
    </div>
  );
};

const HelpScenarioDescriptionAsTextFormatForCustomerSupportImpact = ({ customerSupportImpact }: { customerSupportImpact: CustomerSupportImpact }) => {
  const { description } = customerSupportImpact;

  return (
    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
      {description}
    </Typography>
  );
};

const HelpScenarioDescriptionAsTextFormatForSimpleExplanation = ({ simpleExplanation }: { simpleExplanation: SimpleExplanation }) => {
  const { explanation } = simpleExplanation;

  return (
    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
      {explanation}
    </Typography>
  );
};

const HelpScenarioDescriptionAsListFormatForAgentsNextSteps = ({ agentsNextSteps }: { agentsNextSteps: AgentsNextSteps }) => {
  const { steps } = agentsNextSteps;

  return (
    <ul style={{ paddingLeft: '1.2em', margin: 0 }}>
      {steps.map((item, index) => (
        <li key={index}>
          <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
            {item}
          </Typography>
        </li>
      ))}
    </ul>
  );
};

const HelpScenarioDescriptionAsListFormatForSampleQuestions = ({ sampleQuestions }: { sampleQuestions: SampleQuestion }) => {
  const { questions } = sampleQuestions;

  return (
    <ul style={{ paddingLeft: '1.2em', margin: 0 }}>
      {questions.map((item, index) => (
        <li key={index}>
          <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
            {item}
          </Typography>
        </li>
      ))}
    </ul>
  );
};

const HelpScenarioDescriptionAsListFormatForAdditionalAddressIssues = ({ additionalAddressIssues }: { additionalAddressIssues: ScenarioDescription[] }) => {
  return (
    <ul style={{ paddingLeft: '1.2em', margin: 0 }}>
      {additionalAddressIssues.map(({ description, note }, index) => (
        <li key={index}>
          <div style={{ whiteSpace: 'pre-line' }}>
            {description}
            {note && (
              <>
                {'\n'}
                <Typography variant="body2" color={'error.main'} style={{ fontStyle: 'italic' }}>
                  {note}
                </Typography>
              </>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

const CustomerSupportGuidanceTableRow = ({ rowKey, label, children }: CustomerSupportGuidanceTableRowProps) => {
  return (
    <TableRow key={rowKey}>
      <TableCell style={{ whiteSpace: 'nowrap' }}>
        <div style={{ display: 'inline-block' }}>
          <Typography variant="body2" fontWeight="bold">
            {label}
          </Typography>
        </div>
      </TableCell>
      <TableCell
        style={{
          width: '100%',
          whiteSpace: 'pre-line'
        }}
      >
        {children}
      </TableCell>
    </TableRow>
  );
};

const CustomerSupportGuidanceTableSkeletonRow = ({ rowName }: { rowName: string }) => {
  return (
    <TableRow key={rowName}>
      <TableCell style={{ whiteSpace: 'nowrap' }}>
        <Typography variant={'body2'} fontWeight={'bold'}>
          {rowName}
        </Typography>
      </TableCell>
      <TableCell style={{ width: '100%' }}>
        <Skeleton animation={'wave'} sx={{ flexGrow: 1 }} />
      </TableCell>
    </TableRow>
  );
};

const CustomerSupportGuidanceDataTable = ({ searchResults, isRequestError }: CustomerSupportGuidanceProps) => {
  if (isRequestError === null) {
    return <TableFromCustomerHelpScenario customerHelpScenario={searchResults.customerHelpScenario} />;
  } else {
    return <TableSkeleton />;
  }
};

export default CustomerSupportGuidance;
